import { Inject, Injectable } from '@angular/core';
import { IDataService, REFERRAL_PROGRAM_TYPE } from '../../data/data-service.interface';
import { RbacServiceInterface } from '../../rbac/rbac-service.interface';
import { UntypedFormControl, Validators } from '@angular/forms';
import { FieldModel } from './field.model';
import {
  TextFieldComponent,
  SelectComponent,
  BooleanSelectFieldComponent,
  DatepickerFieldNewComponent,
  TagsFieldComponent,
  SelectWithIconComponent,
  NumberFieldComponent,
} from '@platform500services/p500-ui-kit';
import { CustomValidators } from '../../../validators/validators';
import { CountryRegionComponent } from '../../../components/fields/default/country-region/country-region.component';

@Injectable({
  providedIn: 'root',
})
export class FilterControlsFactory {
  private readonly globalData = this.dataService.getData();

  constructor(
    @Inject('IDataService') private dataService: IDataService,
    @Inject('RbacServiceInterface') public rbacService: RbacServiceInterface
  ) {}

  public getField(type: any, key: string, label?: string, formControl?: UntypedFormControl): FieldModel {
    const control = formControl ? formControl : new UntypedFormControl();

    switch (type) {
      case 'string':
        return new FieldModel(label, key, TextFieldComponent, control);

      case 'boolean':
        return new FieldModel(label, key, BooleanSelectFieldComponent, control);

      case 'boolean_switch':
        return new FieldModel(label, key, BooleanSelectFieldComponent, control, null, 'boolean_switch');

      case 'datetime':
        return new FieldModel(label, key, DatepickerFieldNewComponent, control);

      case 'currency':
        return new FieldModel(label, key, SelectWithIconComponent, control, {
          multiple: true,
          data: this.globalData.currency_list,
        });

      case 'country':
        return new FieldModel(label, key, SelectWithIconComponent, control, {
          multiple: true,
          data: this.globalData.countries,
        });

      case 'advertiser_id':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.advertisers,
        });

      case 'offer_id':
        return new FieldModel(label, key, SelectComponent, control, { multiple: true, data: this.globalData.offers });

      case 'goal_id':
        return new FieldModel(label, key, SelectComponent, control, { multiple: true, data: this.globalData.goals });

      case 'tracking_domain_id':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.tracking_domains,
        });

      case 'affiliate_id':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.affiliates,
        });

      case 'user_id':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.users,
        });

      case 'tag_numbers': {
        const tagNumbersModel = new FieldModel(label, key, TagsFieldComponent, control, { onlyNumbers: true });
        tagNumbersModel.formControl.addValidators(CustomValidators.unsignedIntString);
        return tagNumbersModel;
      }

      case 'number': {
        const numberModel = new FieldModel(label, key, NumberFieldComponent, control);
        numberModel.formControl.addValidators([Validators.max(2147483647), Validators.min(1)]);
        return numberModel;
      }

      case 'earnings_total_sum_filter': {
        const earningTotalSumNumberModel = new FieldModel(label, key, NumberFieldComponent, control, {
          placeholder: $localize`:@@finance_earnings_minimal_sum_payouts:Minimal sum of payouts`,
        });
        earningTotalSumNumberModel.formControl.addValidators([Validators.max(2147483647), Validators.min(1)]);
        return earningTotalSumNumberModel;
      }

      case 'tag_strings':
        return new FieldModel(label, key, TagsFieldComponent, control);

      case 'goal_type':
        return new FieldModel(
          label,
          key,
          SelectComponent,
          control,
          {
            multiple: true,
            data: this.globalData.goal_types,
          },
          type
        );

      case 'payout_model_id':
        return new FieldModel(
          label,
          key,
          SelectComponent,
          control,
          {
            multiple: true,
            data: this.globalData.payout_type,
          },
          type
        );

      case 'revenue_model_id':
        return new FieldModel(
          label,
          key,
          SelectComponent,
          control,
          {
            multiple: true,
            data: this.globalData.revenue_type,
          },
          type
        );
      case 'language':
        return new FieldModel(
          label,
          key,
          SelectWithIconComponent,
          control,
          {
            multiple: true,
            data: this.globalData.languages,
          },
          type
        );

      case 'term_id': {
        return new FieldModel(
          label,
          key,
          SelectComponent,
          control,
          {
            multiple: true,
            data: Object.values(this.globalData.terms).reduce(
              (acc, { id, label: name }) => ({
                ...acc,
                [id]: name,
              }),
              {}
            ),
          },
          type
        );
      }

      case 'invoice_status':
        return new FieldModel(
          label,
          key,
          SelectComponent,
          control,
          {
            multiple: true,
            data: this.globalData.invoice_statuses,
          },
          type
        );

      case 'tree_active_elements':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: false,
          data: {
            all: $localize`:@@common.all:All`,
            active: $localize`:@@common.active:Active`,
            not_active: $localize`:@@common.not_active:Not active`,
          },
        });

      case 'days':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: this.globalData.days,
        });

      case 'hoursOfDayFrom':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: {
            'hourOfDay >= 0': '00:00',
            'hourOfDay >= 1': '01:00',
            'hourOfDay >= 2': '02:00',
            'hourOfDay >= 3': '03:00',
            'hourOfDay >= 4': '04:00',
            'hourOfDay >= 5': '05:00',
            'hourOfDay >= 6': '06:00',
            'hourOfDay >= 7': '07:00',
            'hourOfDay >= 8': '08:00',
            'hourOfDay >= 9': '09:00',
            'hourOfDay >= 10': '10:00',
            'hourOfDay >= 11': '11:00',
            'hourOfDay >= 12': '12:00',
            'hourOfDay >= 13': '13:00',
            'hourOfDay >= 14': '14:00',
            'hourOfDay >= 15': '15:00',
            'hourOfDay >= 16': '16:00',
            'hourOfDay >= 17': '17:00',
            'hourOfDay >= 18': '18:00',
            'hourOfDay >= 19': '19:00',
            'hourOfDay >= 20': '20:00',
            'hourOfDay >= 21': '21:00',
            'hourOfDay >= 22': '22:00',
            'hourOfDay >= 23': '23:00',
          },
        });

      case 'hoursOfDayTo':
        return new FieldModel(label, key, SelectComponent, control, {
          multiple: true,
          data: {
            'hourOfDay < 1': '00:59',
            'hourOfDay < 2': '01:59',
            'hourOfDay < 3': '02:59',
            'hourOfDay < 4': '03:59',
            'hourOfDay < 5': '04:59',
            'hourOfDay < 6': '05:59',
            'hourOfDay < 7': '06:59',
            'hourOfDay < 8': '07:59',
            'hourOfDay < 9': '08:59',
            'hourOfDay < 10': '09:59',
            'hourOfDay < 11': '10:59',
            'hourOfDay < 12': '11:59',
            'hourOfDay < 13': '12:59',
            'hourOfDay < 14': '13:59',
            'hourOfDay < 15': '14:59',
            'hourOfDay < 16': '15:59',
            'hourOfDay < 17': '15:59',
            'hourOfDay < 18': '17:59',
            'hourOfDay < 19': '18:59',
            'hourOfDay < 20': '19:59',
            'hourOfDay < 21': '20:59',
            'hourOfDay < 22': '21:59',
            'hourOfDay < 23': '22:59',
            'hourOfDay < 24': '23:59',
          },
        });

      case 'country_region': {
        return new FieldModel(label, key, CountryRegionComponent, control, {
          multiple: true,
          data: this.globalData.countries,
        });
      }
      case 'referral_program_privacy': {
        return new FieldModel(label, key, SelectComponent, control, {
          data: this.globalData.default_privacy_options,
        });
      }
      case 'referral_program_type': {
        return new FieldModel(label, key, SelectComponent, control, {
          data: REFERRAL_PROGRAM_TYPE,
        });
      }
      case 'conversion_profile_fields_label': {
        const options = this.globalData.conversion_profile_fields;
        const data = Object.keys(options)
          .sort((a, b) => options[a].id - options[b].id)
          .reduce((acc, key) => {
            const field = options[key];
            acc[field.label] = field.label + ' / ' + field.id;
            return acc;
          }, {});
        return new FieldModel(label, key, SelectComponent, control, { data });
      }
      case 'user_activity_log_targets': {
        return new FieldModel(label, key, SelectComponent, control, {
          data: Object.values(this.globalData.user_activity_log_targets),
          multiple: true,
        });
      }
      case 'user_activity_log_actions': {
        return new FieldModel(label, key, SelectComponent, control, {
          data: Object.values(this.globalData.user_activity_log_actions),
          multiple: true,
        });
      }
      case 'user_activity_log_methods': {
        return new FieldModel(label, key, SelectComponent, control, {
          data: Object.values(this.globalData.user_activity_log_methods),
          multiple: true,
        });
      }
    }

    if (type in this.globalData) {
      return new FieldModel(
        label,
        key,
        SelectComponent,
        control,
        {
          multiple: true,
          data: this.globalData[type],
        },
        type
      );
    }

    return new FieldModel(label, key, TextFieldComponent, control);
  }
}
